import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function PatentSearchPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [inputMode, setInputMode] = useState('asin');
  const [isPlainMode, setIsPlainMode] = useState(false);
  const [asin, setAsin] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [plainQuery, setPlainQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState('');
  const [titleClickCount, setTitleClickCount] = useState(0);
  const [aiRewriteDisabled, setAiRewriteDisabled] = useState(false);
  const [showDisableMessage, setShowDisableMessage] = useState(false);

  useEffect(() => {
    if (location.pathname === '/plain') {
      setIsPlainMode(true);
      setInputMode('plain');
    } else {
      setIsPlainMode(false);
      setInputMode('asin');
    }
  }, [location.pathname]);

  const handleTitleClick = () => {
    const newCount = titleClickCount + 1;
    setTitleClickCount(newCount);
    
    if (newCount === 3) {
      setAiRewriteDisabled(true);
      setShowDisableMessage(true);
      setTimeout(() => setShowDisableMessage(false), 3000);
    }
  };

  const extractAndValidateAsin = (input) => {
    const trimmedInput = input.trim();
    
    if (!trimmedInput) {
      return { valid: false, asin: null, error: 'Please enter an ASIN or Amazon URL' };
    }

    let asinValue = trimmedInput;

    if (trimmedInput.includes('amazon.') || trimmedInput.includes('amzn.')) {
      const patterns = [
        /\/dp\/([A-Z0-9]{10})/i,
        /\/gp\/product\/([A-Z0-9]{10})/i,
        /\/product\/([A-Z0-9]{10})/i,
        /[?&]asin=([A-Z0-9]{10})/i,
        /\/([A-Z0-9]{10})(?:[/?]|$)/i
      ];

      for (const pattern of patterns) {
        const match = trimmedInput.match(pattern);
        if (match && match[1]) {
          asinValue = match[1];
          break;
        }
      }
    }

    const asinPattern = /^[A-Z0-9]{10}$/i;
    if (asinPattern.test(asinValue)) {
      return { valid: true, asin: asinValue.toUpperCase(), error: null };
    }

    return { valid: false, asin: null, error: 'Invalid ASIN format. ASIN should be 10 alphanumeric characters.' };
  };

  const handleSearch = async () => {
    setError('');

    let validatedAsin = null;
    if (inputMode === 'asin') {
      const validation = extractAndValidateAsin(asin);
      if (!validation.valid) {
        setError(validation.error);
        return;
      }
      validatedAsin = validation.asin;
      setAsin(validatedAsin);
    }

    if (inputMode === 'manual' && !title.trim()) {
      setError('Please enter a product title');
      return;
    }

    if (inputMode === 'plain' && !plainQuery.trim()) {
      setError('Please enter a search query');
      return;
    }

    setIsSearching(true);

    try {
      const { patentService } = await import('../../services/patentService');
      
      let searchParams;
      let queryText;

      if (inputMode === 'asin') {
        searchParams = { 
          asin: validatedAsin, 
          topK: 500,
          useAiRewrite: false
        };
        queryText = `ASIN: ${validatedAsin}`;
      } else if (inputMode === 'manual') {
        searchParams = { 
          title: title.trim(), 
          description: description.trim(), 
          topK: 500,
          useAiRewrite: aiRewriteDisabled ? false : true 
        };
        queryText = title.trim() + (description.trim() ? ` - ${description.trim()}` : '');
      } else {
        searchParams = { 
          query: plainQuery.trim(), 
          topK: 500,
          useAiRewrite: false 
        };
        queryText = plainQuery.trim();
      }

      const result = await patentService.search(searchParams);
      
      if (result.resultId) {
        const { searchHistoryUtil } = await import('../../utils/searchHistory');
        const historyEntry = {
          query: queryText,
          asin: inputMode === 'asin' ? validatedAsin : null,
          title: inputMode === 'manual' ? title.trim() : null,
          description: inputMode === 'manual' ? description.trim() : null,
          resultId: result.resultId,
        };
        
        if (result.productTitle) {
          historyEntry.productTitle = result.productTitle;
        }
        if (result.productImageUrl) {
          historyEntry.productImageUrl = result.productImageUrl;
        }
        
        searchHistoryUtil.addSearch(historyEntry);
        
        let navigateUrl = `/search?id=${result.resultId}&query=${encodeURIComponent(queryText)}`;
        if (result.productTitle) {
          navigateUrl += `&productTitle=${encodeURIComponent(result.productTitle)}`;
        }
        if (result.productImageUrl) {
          navigateUrl += `&productImageUrl=${encodeURIComponent(result.productImageUrl)}`;
        }
        
        navigate(navigateUrl);
      } else {
        setError('Search completed but no result ID returned');
        setIsSearching(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to initiate search. Please try again.');
      setIsSearching(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center px-4 py-12">
      <div className="w-full max-w-3xl space-y-8">
        <div className="text-center space-y-4">
          <h1 
            onClick={handleTitleClick}
            className="text-4xl font-bold text-white cursor-pointer select-none"
          >
            TadReamk Surveillance
          </h1>
          <p className="text-lg text-gray-300">
            Search for patents using Amazon ASIN or product description
          </p>
        </div>

        {showDisableMessage && (
          <div className="bg-green-500/90 text-white border border-green-400 rounded-2xl px-4 py-3 text-sm shadow-lg text-center animate-fade-in">
            Disable all AI rewrite
          </div>
        )}

        {error && (
          <div className="bg-red-500/90 text-white border border-red-400 rounded-2xl px-4 py-3 text-sm shadow-lg">
            {error}
          </div>
        )}

        <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-xl p-8 space-y-6">
          {!isPlainMode && (
            <div className="flex gap-2 self-start">
              <button
                onClick={() => setInputMode('asin')}
                className={`py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                  inputMode === 'asin'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                }`}
              >
                Search by ASIN
              </button>
              <button
                onClick={() => setInputMode('manual')}
                className={`py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                  inputMode === 'manual'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                }`}
              >
                Describe Product
              </button>
            </div>
          )}

          <div className="space-y-4">
            {inputMode === 'asin' ? (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-200">
                  Amazon ASIN
                </label>
                <input
                  type="text"
                  value={asin}
                  onChange={(e) => setAsin(e.target.value)}
                  placeholder="e.g., B08N5WRWNW"
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-400">
                    Enter the Amazon Standard Identification Number
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">Examples:</span>
                    <button
                      onClick={() => setAsin('B0FS1SHBS5')}
                      className="px-3 py-1 bg-white/10 hover:bg-white/20 text-white text-xs rounded-lg transition-all border border-white/20"
                    >
                      Game Controller
                    </button>
                    <button
                      onClick={() => setAsin('B0CNY1F31S')}
                      className="px-3 py-1 bg-white/10 hover:bg-white/20 text-white text-xs rounded-lg transition-all border border-white/20"
                    >
                      Air Fryer
                    </button>
                  </div>
                </div>
              </div>
            ) : inputMode === 'manual' ? (
              <>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-200">
                    Product Title *
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter product title"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-200">
                    Product Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter detailed product description (optional)"
                    rows="6"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  />
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-200">
                  Search Query
                </label>
                <textarea
                  value={plainQuery}
                  onChange={(e) => setPlainQuery(e.target.value)}
                  placeholder="Enter your search query"
                  rows="6"
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />
              </div>
            )}
          </div>

          <button
            onClick={handleSearch}
            disabled={isSearching}
            className="w-full py-4 px-6 rounded-xl font-bold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 shadow-lg transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          >
            {isSearching ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Searching...
              </span>
            ) : (
              'Search Patents'
            )}
          </button>
        </div>

        <div className="text-center text-sm text-gray-400">
          <p>
            Our AI-powered search will find relevant patents based on your input
          </p>
        </div>
      </div>
    </div>
  );
}


